import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  changeSelectedRows,
  selectedReport,
  toggleModal,
  setUseReportIntervalFilter,
} from "app/store/transmission/slice/transmissionSlice";
import { transmissionTableColumnVisibility } from "utils/transmissionTable";
import {
  buildTransmissionFilterQuery,
  buildTransmissionReportsUrl,
  parseStoredSorting,
  resolveFollowingHospitals,
  resolveFollowingPhysicians,
  resolveMyPatientsFilter,
  resolveTransmissionSorting,
  serializeSortingForStorage,
  shouldUseReportIntervalFilter,
} from "utils/transmissionTable.query";
import useDebouncedValue from "app/hooks/useDebouncedValue";
import _, { debounce } from "lodash";

// Long enough to fold a run of filter clicks into one request, short enough not to feel
// like lag. The first value is not delayed, so a page load never waits on it.
const FILTER_SETTLE_MS = 400;

const useTransmissionTable = (
  role,
  view,
  setRowSelection,
  { patch } = {}
) => {
  const {
    isShowFilter,
    physiciansFilter,
    isFilterAlert,
    isHideBilledFilter,
    isMyPatientsFilter,
    transmissionType,
    reportType,
    totalSelectedRows,
    reportInterval,
    useReportIntervalFilter,
    priorityType,
    showSavedReports,
    showAuditedReports,
  } = useSelector((state) => state.transmission);
  const { currentUser } = useSelector((state) => state.auth);
  const { accounts, accountsMapper } = useSelector((state) => state.account);

  const transmissionTableColumnKey = `transmissionTableColumn:${currentUser?.userId}`;
  const transmissionTableSortingKey = `transmissionTableSorting:${currentUser?.role}:${currentUser?.userId}`;

  const dispatch = useDispatch();

  const userRole = localStorage.getItem("userRole");
  const clinicName = localStorage.getItem("clinicName");

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  // The order the user left the table in, settled before the first request rather than
  // restored and then corrected.
  const storedSorting = useMemo(
    () =>
      resolveTransmissionSorting(
        parseStoredSorting(localStorage.getItem(transmissionTableSortingKey))
      ),
    [transmissionTableSortingKey]
  );

  const [sorting, setSorting] = useState(storedSorting);
  const initialSorting = useRef(storedSorting);
  const [columnFilters, setColumnFilters] = useState([]);
  const [search, setSearch] = useState("");

  // Each worklist starts unfiltered. Cleared as the tab changes rather than a render
  // later, so the switch asks for one thing once.
  const [filteredView, setFilteredView] = useState(view);

  if (view !== filteredView) {
    setFilteredView(view);

    if (columnFilters.length > 0) setColumnFilters([]);
  }

  const onSearchChange = useMemo(
    () =>
      debounce((value) => {
        setSearch(value || undefined);
      }, 1500),
    []
  );

  useEffect(() => () => onSearchChange.cancel(), [onSearchChange]);

  // The table hands over either the next value or a reducer; the sort rules apply on the
  // way in, so an order the list cannot serve is never requested.
  const onSortingChange = useCallback((next) => {
    setSorting((current) =>
      resolveTransmissionSorting(
        typeof next === "function" ? next(current) : next
      )
    );
  }, []);

  // Which physicians and hospitals this user follows, and whether they are on their own
  // patients — all of it saved for them, so it belongs to the first render.
  const physicians = useMemo(
    () =>
      physiciansFilter ??
      resolveFollowingPhysicians(localStorage.getItem("followingPhysicians")),
    [physiciansFilter]
  );

  const followingHospitals = useMemo(
    () =>
      resolveFollowingHospitals(
        currentUser?.followingHospitals,
        currentUser?.isFollowingHospital
      ),
    [currentUser?.followingHospitals, currentUser?.isFollowingHospital]
  );

  const myPatients = useMemo(
    () =>
      isMyPatientsFilter ??
      resolveMyPatientsFilter({
        view,
        showOnlyAssignedReports:
          accountsMapper?.[clinicName]?.settings?.show_only_assigned_reports,
        myPatientsPreference: currentUser?.user_prefs?.my_patients_toggled,
        storedMyPatientsToggled: localStorage.getItem(
          `myPatientsToggled:${currentUser?.userId}`
        ),
      }),
    [
      isMyPatientsFilter,
      view,
      accountsMapper,
      clinicName,
      currentUser?.user_prefs?.my_patients_toggled,
      currentUser?.userId,
    ]
  );

  // A date column filter picks its own window, so the interval selector stands down.
  const useReportInterval = shouldUseReportIntervalFilter(columnFilters);

  // The interval selector reads this to decide whether to show itself.
  useEffect(() => {
    if (useReportInterval !== useReportIntervalFilter) {
      dispatch(setUseReportIntervalFilter(useReportInterval));
    }
  }, [useReportInterval, useReportIntervalFilter, dispatch]);

  const filterQuery = useMemo(
    () =>
      buildTransmissionFilterQuery({
        view,
        physiciansFilter: physicians,
        reportType,
        isFilterAlert,
        isHideBilledFilter,
        isMyPatientsFilter: myPatients,
        transmissionType,
        priorityType,
        showSavedReports,
        showAuditedReports,
        columnFilters,
        followingHospitals,
      }),
    [
      view,
      physicians,
      reportType,
      isFilterAlert,
      isHideBilledFilter,
      myPatients,
      transmissionType,
      priorityType,
      showSavedReports,
      showAuditedReports,
      columnFilters,
      followingHospitals,
    ]
  );

  const filters = useDebouncedValue(filterQuery, FILTER_SETTLE_MS, view);

  const [initialColumnVisibility, setInitialColumnVisibility] = useState({});
  const [columnVisibility, setColumnVisibility] = useState({});

  useEffect(() => {
    let initialTableColumn = transmissionTableColumnVisibility(
      accounts,
      userRole,
      currentUser
    );

    const userInitialTableColumn = JSON.parse(
      localStorage.getItem(transmissionTableColumnKey)
    );

    initialTableColumn = {
      ...initialTableColumn,
      ...userInitialTableColumn,
    };

    if (Object.keys(initialTableColumn)?.length > 0) {
      setColumnVisibility(initialTableColumn);
      setInitialColumnVisibility(initialTableColumn);
    }
  }, [view, accounts, userRole, currentUser]);

  useEffect(() => {
    const isColumnEqual = _.isEqual(initialColumnVisibility, columnVisibility);

    if (!isColumnEqual) {
      localStorage.setItem(
        transmissionTableColumnKey,
        JSON.stringify(columnVisibility)
      );
    }
  }, [columnVisibility]);

  useEffect(() => {
    if (sorting.length === 0) return;
    if (_.isEqual(initialSorting.current, sorting)) return;

    const finalSortingObject = serializeSortingForStorage(sorting);

    if (!finalSortingObject) return;

    localStorage.setItem(
      transmissionTableSortingKey,
      JSON.stringify(finalSortingObject)
    );
  }, [sorting, transmissionTableSortingKey]);

  const onRowClick = (rowData) => {
    dispatch(
      selectedReport({
        id: rowData?._id,
        isLegacyReport: rowData?.legacy ?? true,
        reportDetail: rowData,
      })
    );
    dispatch(toggleModal({ type: "reportDetail", value: true }));
    dispatch(changeSelectedRows({}));
    setRowSelection?.({});
  };

  const handleSelection = (rows) => {
    const reports = rows?.map(({ _id }) => _id);
    dispatch(changeSelectedRows({ reports, selectedRows: rows.length }));
  };

  // The whole request, decided in one place. Two states that would ask the server the
  // same question produce the same string, so they cost one request between them.
  const url = useMemo(
    () =>
      buildTransmissionReportsUrl({
        userRole,
        view,
        page: pagination.pageIndex,
        pageSize: pagination.pageSize,
        search,
        filters,
        sorting,
        reportInterval,
        useReportIntervalFilter: useReportInterval,
        patch,
      }),
    [
      userRole,
      view,
      pagination.pageIndex,
      pagination.pageSize,
      search,
      filters,
      sorting,
      reportInterval,
      useReportInterval,
      patch,
    ]
  );

  // Every route is held until the profile and the accounts are in the store, so this only
  // catches a caller that skipped that.
  const isReady = Boolean(view && userRole && currentUser?.userId);

  return {
    url,
    isReady,
    search,
    sorting,
    dispatch,
    accounts,
    userRole,
    pagination,
    onRowClick,
    isShowFilter,
    columnFilters,
    setPagination,
    reportInterval,
    onSearchChange,
    onSortingChange,
    handleSelection,
    setColumnFilters,
    columnVisibility,
    totalSelectedRows,
    setColumnVisibility,
    useReportIntervalFilter: useReportInterval,
  };
};

export default useTransmissionTable;
